import type { TranslationSource } from '@processpuzzle/util';

/**
 * Transloco scope of this library. The translations live in
 * `libs/platform-subscription/frontend/src/assets/i18n/platform_subscription/*.json` and are
 * published with the package; a consuming application copies them to
 * `assets/i18n/platform_subscription` (see `apps/processpuzzle-biz-frontend/project.json`).
 *
 * The alias is spelled out wherever this is registered, and that is not a style choice: transloco
 * camel-cases the default alias, which would turn `platform_subscription` into
 * `platformSubscription` and silently miss every key below it. Both the underscore and a hyphen
 * suffer the same fate, so there is no spelling of the scope that makes the default safe.
 */
export const PLATFORM_SUBSCRIPTION_TRANSLOCO_SCOPE = 'platform_subscription';

/** Key root of the sign-up funnel: the stepper, its five steps and the confirmation page. */
export const SIGNUP_I18N_SCOPE = `${PLATFORM_SUBSCRIPTION_TRANSLOCO_SCOPE}.signup`;

/** Key root of the customer portal: the shell, the status card, invoices and the cancel dialog. */
export const PORTAL_I18N_SCOPE = `${PLATFORM_SUBSCRIPTION_TRANSLOCO_SCOPE}.portal`;

/**
 * Key root of the invoiced-party form, which both surfaces render.
 *
 * Under neither `signup` nor `portal`, because it is the same eight fields in both: the funnel
 * collects them and the portal corrects them. Nesting it under one would have meant the other
 * reading keys out of a screen it is not part of, or a second set of labels for one form.
 */
export const BILLING_PARTY_I18N_SCOPE = `${PLATFORM_SUBSCRIPTION_TRANSLOCO_SCOPE}.billing_party`;

/**
 * Where this library's transloco bundles come from when the application ships without its assets.
 *
 * Spread into the application's `TRANSLATION_SOURCE_REGISTRY`. Normally the scope arrives as static
 * files — the build copies them into `assets/i18n/platform_subscription` and the loader tries the
 * asset first — so this is reached only by a host that skips that copy step.
 *
 * `serviceRootKey` is this gateway's own root rather than Admin's: the Biz frontend talks to
 * `processpuzzle-biz-backend` and nothing else, which is the whole arrangement the gateway exists
 * for. `segment` is empty because every path in platform-subscription-api.yaml sits directly under
 * the API root — there is no `/platform` or `/admin` prefix to prepend, unlike the two libraries
 * this pattern is copied from. There is no translations endpoint behind it either; the entry exists
 * so that adding one later needs no change on this side.
 */
export const PLATFORM_SUBSCRIPTION_TRANSLATION_SOURCE: TranslationSource = {
  scopes: [PLATFORM_SUBSCRIPTION_TRANSLOCO_SCOPE],
  serviceRootKey: 'SUBSCRIPTION_SERVICE_ROOT',
  segment: '',
};
